import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Toaster as Sonner } from "./components/ui/sonner";
import { Toaster } from "./components/ui/toaster";
import { TooltipProvider } from "./components/ui/tooltip";
import { ThemeProvider } from "./lib/theme";
import Index from "./routes/index.tsx";
import NotFound from "./routes/notfound.tsx";
import Connect from "./routes/connect.tsx";
import Preview from "./routes/preview.tsx";
import RegistryPage from "./routes/registry.tsx";
import McpDetailsPage from "./routes/mcp-details.tsx";
import PrivacyPage from "./routes/privacy.tsx";
import TermsPage from "./routes/terms.tsx";
import AuthErrorPage from "./routes/auth-error.tsx";
import AdminPage from "./routes/admin.tsx";
import PricingPage from "./routes/pricing.tsx";
import DashboardPage from "./routes/dashboard.tsx";
import RefundPolicyPage from "./routes/refund-policy.tsx";
import OAuthCallbackPage from "./routes/oauth-callback.tsx";
import McpLandingPage from "./routes/mcp-landing.tsx";
import GuidePage from "./routes/guide.tsx";
import BlogPage from "./routes/blog.tsx";
import BlogPostPage from "./routes/blog-post.tsx";
import StatusPage from "./routes/status.tsx";
import SharePage from "./routes/share.tsx";
import { useEffect } from "react";
import { trackPage } from "./analytics";

// Sentry-aware Route for automatic transaction naming per route
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
    },
  },
});

// ── Separate component so useLocation works inside BrowserRouter ──────────
function AppRoutes() {
  const location = useLocation();   // ← inside component, inside BrowserRouter

  useEffect(() => {
    trackPage(location.pathname);
  }, [location]);

  return (
    <SentryRoutes>
      <Route path="/" element={<Index />} />
      <Route path="/registry" element={<RegistryPage />} />
      <Route path="/registry/:slug" element={<McpDetailsPage />} />
      <Route path="/preview" element={<Preview />} />
      <Route path="/connect" element={<Connect />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/auth/error" element={<AuthErrorPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/refund-policy" element={<RefundPolicyPage />} />
      <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
      {/* SEO landing pages */}
      <Route path="/mcp/:slug" element={<McpLandingPage />} />
      <Route path="/guides/:slug" element={<GuidePage />} />
      {/* Blog */}
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/share/:token" element={<SharePage />} />
      <Route path="*" element={<NotFound />} />
    </SentryRoutes>
  );
}

const App = () => (
  <Sentry.ErrorBoundary
    fallback={({ error, resetError }) => (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-8 text-center">
        <p className="text-xl font-semibold text-foreground">Something went wrong</p>
        <p className="text-sm text-muted-foreground max-w-sm">
          {error instanceof Error ? error.message : "An unexpected error occurred."}
        </p>
        <button
          onClick={resetError}
          className="px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium"
        >
          Try again
        </button>
      </div>
    )}
  >
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </Sentry.ErrorBoundary>
);

export default App;